import { NAMESPACES, expandIri } from './namespaces.js';

/**
 * How a term's value is carried in JSON-LD. `id` means the value is a node
 * reference and therefore participates in referential-integrity checking; the
 * rest are literals.
 */
export type TermValueKind = 'id' | 'string' | 'number' | 'boolean';

export interface TermDefinition {
  /** Canonical key used in serialised JSON-LD and in the parsed node objects. */
  readonly term: string;
  /** Full IRI the term resolves to. */
  readonly iri: string;
  readonly kind: TermValueKind;
  /**
   * Canonical cardinality. JSON-LD treats a single value and a one-element array
   * as identical, so normalising every many-valued term to an array loses no
   * meaning and removes an entire class of `Array.isArray` branching downstream.
   */
  readonly many: boolean;
}

function dpem(term: string, kind: TermValueKind, many = false): TermDefinition {
  return { term, iri: `${NAMESPACES.dpem}${term}`, kind, many };
}

function borrowed(term: string, iri: string, kind: TermValueKind, many = false): TermDefinition {
  return { term, iri, kind, many };
}

/**
 * The complete DPSE property vocabulary.
 *
 * Legacy terms (everything in `scratchpad/method-standard.jsonld` and
 * `method-agentic.jsonld`) and the promoted first-class terms live in one table
 * because the context, the parser, the expander and the reference walker must
 * never disagree about a term's IRI, kind or cardinality.
 */
export const TERMS = {
  // --- borrowed vocabulary -------------------------------------------------
  name: borrowed('name', `${NAMESPACES.rdfs}label`, 'string'),
  description: borrowed('description', `${NAMESPACES.schema}description`, 'string'),

  // --- containment / structure --------------------------------------------
  hasPhase: dpem('hasPhase', 'id', true),
  hasStage: dpem('hasStage', 'id', true),
  hasWorkPractice: dpem('hasWorkPractice', 'id', true),
  hasActivity: dpem('hasActivity', 'id', true),
  hasRole: dpem('hasRole', 'id', true),
  hasDeliverable: dpem('hasDeliverable', 'id', true),
  hasLens: dpem('hasLens', 'id', true),
  hasCeremony: dpem('hasCeremony', 'id', true),
  hasTool: dpem('hasTool', 'id', true),
  hasAsset: dpem('hasAsset', 'id', true),
  hasAgent: dpem('hasAgent', 'id', true),
  /**
   * A method's agent-skill and MCP-server catalogues. Present for the same reason
   * `hasTool` is: the studio lets an author assemble the catalogue a method draws
   * on, and a method that lists its tools and agents but not the skills and
   * servers they run on would describe the agentic SDLC with two of its five
   * catalogues missing.
   */
  hasAgentSkill: dpem('hasAgentSkill', 'id', true),
  hasMcpServer: dpem('hasMcpServer', 'id', true),
  hasGate: dpem('hasGate', 'id', true),
  inPhase: dpem('inPhase', 'id'),
  inWorkPractice: dpem('inWorkPractice', 'id'),
  parentPractice: dpem('parentPractice', 'id'),

  // --- flow ----------------------------------------------------------------
  precedes: dpem('precedes', 'id', true),
  dependsOn: dpem('dependsOn', 'id', true),
  /** Deliberate backward edge. Never counts as a dependency cycle. */
  loopsTo: dpem('loopsTo', 'id', true),

  // --- assignment ----------------------------------------------------------
  performedBy: dpem('performedBy', 'id', true),
  produces: dpem('produces', 'id', true),
  consumes: dpem('consumes', 'id', true),
  usesLens: dpem('usesLens', 'id', true),
  usesTool: dpem('usesTool', 'id', true),
  usesAsset: dpem('usesAsset', 'id', true),
  /**
   * Activity → catalogued agent, agent-skill and MCP server, the direct
   * counterparts of `usesTool`.
   *
   * The indirect chain already exists — an activity's `performedBy` role can be
   * `realisedByAgent`, and that agent `hasSkill` and `usesMcpServer` — but it
   * cannot say "this activity uses the code-review skill" without first minting a
   * role to hang the agent off. The studio lets an author attach any of the five
   * catalogues straight to an activity, so all five need a term: without these
   * two, an agent-skill or MCP-server attachment had no serialised form and
   * vanished on export, which is the same defect `realisedByAgent` was added to
   * fix one level up.
   */
  usesAgent: dpem('usesAgent', 'id', true),
  usesAgentSkill: dpem('usesAgentSkill', 'id', true),
  governedByGate: dpem('governedByGate', 'id'),
  communication: dpem('communication', 'id', true),
  agentRole: dpem('agentRole', 'id'),
  humanRole: dpem('humanRole', 'id'),
  implementsRole: dpem('implementsRole', 'id'),
  /**
   * Role → catalogued agent. Distinct from `implementsRole` (role → role): an
   * agentic role *is* a method role, and the agent is the catalogue entry that
   * realises it. Without this term `roles.agent_id` had no serialised form and
   * an agentic method exported as if its agents were unassigned.
   */
  realisedByAgent: dpem('realisedByAgent', 'id'),
  hasSkill: dpem('hasSkill', 'id', true),
  usesMcpServer: dpem('usesMcpServer', 'id', true),

  // --- variants ------------------------------------------------------------
  variantOf: dpem('variantOf', 'id'),

  // --- deliverables and their versioned bodies -----------------------------
  templateOf: dpem('templateOf', 'id'),
  hasTemplateVersion: dpem('hasTemplateVersion', 'id', true),
  currentTemplateVersion: dpem('currentTemplateVersion', 'id'),
  templateOverride: dpem('templateOverride', 'id'),

  // --- client provenance ---------------------------------------------------
  /**
   * Where a catalogue node came from. `"client"` — the only value emitted — means
   * the entry was brought into an adoption session's own catalogue rather than
   * taken from the platform's. Absence means the platform's, which is why there is
   * no `"platform"` spelling: every method published before adoption catalogues
   * existed would otherwise have to be rewritten to keep its meaning.
   */
  provenance: dpem('provenance', 'string'),
  /** The client or session a client node was brought under. */
  broughtBy: dpem('broughtBy', 'string'),
  /**
   * The platform entry a client node was brought as a substitute for.
   *
   * A **string**, not a reference, and that is deliberate: the entry it names is
   * exactly the one the client is *not* using, so it is not in the published graph
   * and MTH-001 (dangling reference, error severity) would make every substitution
   * unpublishable. The value is still a canonical id, so a reader can resolve it
   * against the platform catalogue.
   */
  standsInFor: dpem('standsInFor', 'string'),
  /**
   * The method's client nodes, listed on the method node.
   *
   * A reference list, and safe as one because it is derived from the nodes the
   * export already contains — see `catalogueLists`' sibling in the serialiser. It
   * exists so a reader can answer "what in this method is the client's?" without
   * scanning every node for a `provenance`.
   */
  clientContributions: dpem('clientContributions', 'id', true),

  // --- tailoring -----------------------------------------------------------
  appliesTo: dpem('appliesTo', 'id'),
  decidedBy: dpem('decidedBy', 'id'),
  decision: dpem('decision', 'string'),
  rationale: dpem('rationale', 'string'),

  // --- legacy scalars (present in the seed graphs) -------------------------
  order: dpem('order', 'number'),
  isMetaStage: dpem('isMetaStage', 'boolean'),
  isAgentRole: dpem('isAgentRole', 'boolean'),
  agenticShift: dpem('agenticShift', 'string'),
  automatedCheck: dpem('automatedCheck', 'string'),
  humanApproval: dpem('humanApproval', 'string'),
  exitCriteria: dpem('exitCriteria', 'string'),
  templateFile: dpem('templateFile', 'string'),
  /**
   * Many-valued: a role's responsibilities are a list, which is how the studio
   * authors them and how `roles.responsibilities` stores them. The seed graphs
   * write a single string; `parse.ts` normalises that to a one-element array, so
   * both spellings mean the same thing.
   */
  responsibilities: dpem('responsibilities', 'string', true),
  /** Preserved as a string even though Phase is now an entity (§3). */
  phase: dpem('phase', 'string'),
  /** Preserved as a string even though WorkPractice is now an entity (§3). */
  workPractice: dpem('workPractice', 'string'),
  workStream: dpem('workStream', 'string', true),
  mandatory: dpem('mandatory', 'boolean'),
  reviewEquivalent: dpem('reviewEquivalent', 'string'),
  workStreamMapping: dpem('workStreamMapping', 'string'),
  /** A lens's review prompts. Many-valued: `lenses.checklist` is a list. */
  checklist: dpem('checklist', 'string', true),
  when: dpem('when', 'string'),
  /** Who a communication artifact is for, and the shape it takes. */
  audience: dpem('audience', 'string'),
  format: dpem('format', 'string'),
  effortShift: dpem('effortShift', 'string'),
  cycleTimeDelta: dpem('cycleTimeDelta', 'string'),
  automation: dpem('automation', 'string'),

  // --- authoring additions -------------------------------------------------
  /** Long-form markdown body authored in the studio. */
  writeup: dpem('writeup', 'string'),
  status: dpem('status', 'string'),
  version: dpem('version', 'number'),
  body: dpem('body', 'string'),
  category: dpem('category', 'string'),
  purpose: dpem('purpose', 'string'),
  inputs: dpem('inputs', 'string', true),
  outputs: dpem('outputs', 'string', true),
  capability: dpem('capability', 'string', true),
  links: dpem('links', 'string', true),
  attachments: dpem('attachments', 'string', true),
  screenshots: dpem('screenshots', 'string', true),
  endpoint: dpem('endpoint', 'string'),
  transport: dpem('transport', 'string'),
  model: dpem('model', 'string'),
  revision: dpem('revision', 'number'),
} as const satisfies Record<string, TermDefinition>;

export type TermName = keyof typeof TERMS;

export const TERM_LIST: readonly TermDefinition[] = Object.values(TERMS);

/** Terms whose values are node references, in canonical order. */
export const REFERENCE_TERMS: readonly TermDefinition[] = TERM_LIST.filter((t) => t.kind === 'id');

/**
 * Every spelling of a term we accept on input, mapped to its definition:
 * the alias (`phase`), the compact prefixed form the seed files actually use
 * (`dpem:phase`) and the full IRI. One table, so `dpem:isMetaStage` in the
 * agentic file and `isMetaStage` in the standard file parse identically.
 */
export const TERM_LOOKUP: ReadonlyMap<string, TermDefinition> = (() => {
  const lookup = new Map<string, TermDefinition>();
  for (const definition of TERM_LIST) {
    lookup.set(definition.term, definition);
    lookup.set(definition.iri, definition);
    const compact = compactOf(definition.iri);
    if (compact !== undefined) lookup.set(compact, definition);
  }
  return lookup;
})();

function compactOf(iri: string): string | undefined {
  for (const [prefix, base] of Object.entries(NAMESPACES)) {
    if (iri.startsWith(base)) return `${prefix}:${iri.slice(base.length)}`;
  }
  return undefined;
}

export function lookupTerm(key: string): TermDefinition | undefined {
  return TERM_LOOKUP.get(key) ?? TERM_LOOKUP.get(expandIri(key));
}

/** Canonical key order inside a serialised node: identity, label, then A→Z. */
export const NODE_KEY_PRIORITY: readonly string[] = ['@id', '@type', 'name', 'description'];

export function nodeKeyRank(key: string): number {
  const index = NODE_KEY_PRIORITY.indexOf(key);
  return index < 0 ? NODE_KEY_PRIORITY.length : index;
}
